import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { firestore } from '../firebaseConfig'; // Firestore setup
import moment from 'moment-timezone';
import { getWordsData } from './utils';
import { pickExampleSentenceIdForWord } from './words';
import { remainingReviewAllowance, reviewDueCutoff, UNLIMITED } from '../utils/srs';

// Main function to fetch review words
export const fetchReviewWordsSessionItems = async (userId, userData, reviewedWordsData, isSubscribed, learnedSentenceIds = null) => {
  try {
    if (!userId || !userData) {
      throw new Error('Missing required parameters: userId and userData');
    }

    if (userData.settings?.vacation_started_at) {
      return []; // vacation
    }

    const userTimezone = userData.settings?.timezone || 'UTC';
    const todayDate = moment().tz(userTimezone).format('YYYY-MM-DD');

    const toReviewCount = remainingReviewAllowance(userData.settings?.words_reviews_per_day, reviewedWordsData?.[todayDate]);

    // Allowance already spent. Returning early also keeps limit(0) out of the
    // query below — the SDK rejects a non-positive limit.
    if (toReviewCount === 0) {
      return [];
    }

    const currTime = reviewDueCutoff(userData.settings?.review_frequency, userTimezone);

    const userWordsCollectionRef = collection(firestore, 'users', userId, 'user_words');
    const wordsToReview = [];

    // Set the level condition based on subscription status

    let levelCondition;
    if (isSubscribed) {
      levelCondition = where('level', '>=', 0); // Subscribed users can review all levels (0-5)
    } else {
      levelCondition = where('level', 'in', [0, 5]); // Non-subscribed users can only review levels 0 and 5
    }

    // Query to fetch words that need to be reviewed before today, limited by the number to review
    let querySnapshot;
    const baseQuery = query(
      userWordsCollectionRef,
      where('next_review', '<=', currTime),
      where('srs_stage', '<', userData.settings?.review_burned_items ? 8 : 7),
      where('skipped', '==', false),
      levelCondition, // Apply level condition based on subscription status
      orderBy('next_review')
    );

    if (toReviewCount === UNLIMITED) {
      querySnapshot = await getDocs(baseQuery);
    } else {
      querySnapshot = await getDocs(query(baseQuery, limit(toReviewCount)));
    }

    querySnapshot.forEach((docSnapshot) => {
      wordsToReview.push({
        id: docSnapshot.id,
        word_id: docSnapshot.data().word_ref,
        ...docSnapshot.data(),
      });
    });

    const wordsData = await getWordsData(wordsToReview);

    // Attach a random example sentence ID to each word so the front-face
    // "hear in context" button knows whether examples exist before any click
    const wordsWithExamples = await Promise.all(
      wordsData.map(async (word) => {
        try {
          const id = await pickExampleSentenceIdForWord(word.id, learnedSentenceIds);
          return { ...word, exampleSentenceId: id };
        } catch {
          return { ...word, exampleSentenceId: null };
        }
      })
    );

    return wordsWithExamples;
  } catch (error) {
    console.error('Error fetching review words:', error);
    throw error;
  }
};
